/**
 * LeadMagnetSection — email capture for "Prvý dom bez chýb" PDF guide.
 * Posts to POST /api/leads/guide-download. Shows a thank-you state on success.
 *
 * Usage: drop as a full-width section anywhere on the public site.
 *   <LeadMagnetSection source="homepage" />
 *   <LeadMagnetSection source="pillar" variant="compact" />
 */
import { useState } from "react";
import { BookOpen, Download, CheckCircle2, Shield, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = {
  "prvy-dom": {
    eyebrow: "Zdarma PDF · 10 kapitol · Edícia 2026",
    defaultTitle: (
      <>Prvý dom bez chýb — <span className="text-[#00E5FF]">10 vecí</span>, ktoré vám nikto nepovie.</>
    ),
    defaultSubtitle:
      "15 rokov reálnej skúsenosti stavby domov v Bratislave a Podunajsku, zhrnutých do 10 kapitol. Každá s konkrétnym € príkladom z našich projektov (Dunajská Lužná, Lehnice, Blahová, Janíky, Vrakuňa).",
    benefits: [
      "Prečo projekt za 1 500 € vám vybaví len úver (ale nie stavbu)",
      "Pozemok 'so sieťami' — reálne +6 500–13 000 € za prípojky",
      "Zámková dlažba sa prepadne, ak podklad nie je 20 cm — foto podkladu je povinné",
      "Nový stavebný zákon 2024/2026 — čo zmeniť v projekte, aby neprišla pokuta 5 000 €",
    ],
  },
  "rekonstrukcia": {
    eyebrow: "Zdarma PDF · 10 pastí · Rekonštrukcia 2026",
    defaultTitle: (
      <>Rekonštrukcia bez prekvapení — <span className="text-[#00E5FF]">10 pastí</span>, ktoré prejavia prvý rok.</>
    ),
    defaultSubtitle:
      "Kupujete starší dom alebo byt? 10 skrytých problémov, ktoré sa objavia až po kúpe — a konkrétne € prípady, koľko to stálo klientov v Senci, Blahovej, Rovinke a Petržalke.",
    benefits: [
      "Statik za 180 € pred kúpou vám môže ušetriť 22 000 € na kúpnej cene",
      "Azbest v starej streche — povinná likvidácia + ako nespraviť pokutu 30 000 €",
      "Olovené vodovodné rúry — 1 cm vnútorný priemer vám zrádza bez varovania",
      "Nový stavebný zákon 2026 — kedy je rekonštrukcia LEN ohláška, kedy povolenie",
    ],
  },
};

function trackLead(source, variant) {
  try {
    if (typeof window !== "undefined" && typeof window.__smTrack === "function") {
      window.__smTrack("lead_magnet_submit", `${variant || "prvy-dom"}:${source || "unknown"}`);
    }
  } catch {
    /* ignore */
  }
}

function getVisitorId() {
  try {
    return localStorage.getItem("sm_visitor_id") || null;
  } catch {
    return null;
  }
}

export default function LeadMagnetSection({
  source = "homepage",
  variant = "prvy-dom",
  variantKey,          // alias for variant
  copy = {},           // optional overrides: { eyebrow, title, subtitle, benefits }
  compact = false,
  // Legacy props
  title: titleProp,
  subtitle: subtitleProp,
}) {
  const resolvedVariant = VARIANTS[variantKey || variant] ? (variantKey || variant) : "prvy-dom";
  const v = VARIANTS[resolvedVariant];
  const eyebrow = copy.eyebrow || v.eyebrow;
  const title = copy.title || titleProp || v.defaultTitle;
  const subtitle = copy.subtitle || subtitleProp || v.defaultSubtitle;
  const benefits = Array.isArray(copy.benefits) && copy.benefits.length ? copy.benefits : v.benefits;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !name || !consent) {
      setMessage("Vyplňte prosím meno a e-mail (a odsúhlaste zaslanie).");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch(`${API}/api/leads/guide-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          source,
          variant: resolvedVariant,
          visitor_id: getVisitorId(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setStatus("success");
      setMessage(data.message || "Hotovo. Skontrolujte si e-mail.");
      trackLead(source, resolvedVariant);
    } catch (err) {
      setStatus("error");
      setMessage("Nepodarilo sa odoslať. Skúste to prosím znova alebo zavolajte 0907 64 99 64.");
    }
  };

  if (status === "success") {
    return (
      <section
        className={`relative overflow-hidden ${compact ? "py-10" : "py-20"}`}
        data-testid="lead-magnet-success"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/8 via-transparent to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 size={28} className="text-[#00E5FF]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
            Sprievodcu máte v e-maili.
          </h2>
          <p className="text-zinc-300 max-w-xl mx-auto">
            {message} Ak PDF neprišiel za 5 minút, pozrite si spam priečinok alebo
            nám napíšte na <a href="mailto:info@supermajstri.sk" className="text-[#00E5FF]">info@supermajstri.sk</a>.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a href="tel:+421907649964" className="btn-primary" data-testid="lead-magnet-call-after">
              Zavolať teraz: 0907 64 99 64
            </a>
            <a href="#kontakt" className="btn-secondary">Nezáväzný plán</a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`relative overflow-hidden ${compact ? "py-14" : "py-24"} section-dark`}
      data-testid={`lead-magnet-${source}`}
      id="pdf-sprievodca"
    >
      <div className="absolute inset-0 tech-grid opacity-20 pointer-events-none" />
      <div className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          {/* LEFT — pitch */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/12 border border-[#00E5FF]/40 flex items-center justify-center">
                <BookOpen size={16} className="text-[#00E5FF]" />
              </div>
              <span className="mono text-xs text-[#00E5FF] uppercase tracking-[0.25em]">
                {eyebrow}
              </span>
            </div>

            <h2 className={`${compact ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"} font-black tracking-tight leading-[1.05]`}>
              {title}
            </h2>

            <p className="text-lg text-zinc-300 mt-5 max-w-xl font-light">
              {subtitle}
            </p>

            <ul className="mt-7 space-y-2.5">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-200">
                  <Zap size={14} className="text-[#00E5FF] mt-1 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-center gap-5 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><Shield size={12} /> Žiadny spam</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> 1 PDF, 1 e-mail</span>
              <span className="flex items-center gap-1.5"><Download size={12} /> ~ 15 strán</span>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="lg:col-span-2">
            <form
              onSubmit={submit}
              className="glass-card p-6 lg:p-7 border border-[#00E5FF]/25 space-y-4"
              data-testid="lead-magnet-form"
            >
              <div>
                <label htmlFor={`lm-name-${source}`} className="block text-xs mono text-zinc-400 uppercase tracking-widest mb-1.5">
                  Meno
                </label>
                <input
                  id={`lm-name-${source}`}
                  type="text"
                  required
                  autoComplete="given-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ján Novák"
                  className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 transition-colors"
                  data-testid="lead-magnet-input-name"
                />
              </div>

              <div>
                <label htmlFor={`lm-email-${source}`} className="block text-xs mono text-zinc-400 uppercase tracking-widest mb-1.5">
                  E-mail
                </label>
                <input
                  id={`lm-email-${source}`}
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="meno@priklad.sk"
                  className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 transition-colors"
                  data-testid="lead-magnet-input-email"
                />
              </div>

              <div>
                <label htmlFor={`lm-phone-${source}`} className="block text-xs mono text-zinc-400 uppercase tracking-widest mb-1.5">
                  Telefón <span className="text-zinc-600 normal-case tracking-normal">(voliteľné)</span>
                </label>
                <input
                  id={`lm-phone-${source}`}
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+421 907 ..."
                  className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 transition-colors"
                  data-testid="lead-magnet-input-phone"
                />
              </div>

              <label className="flex items-start gap-2.5 text-xs text-zinc-400 cursor-pointer select-none pt-1">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[#00E5FF]"
                  data-testid="lead-magnet-consent"
                />
                <span>
                  Súhlasím so zaslaním PDF a prípadnou nadväznou komunikáciou od SuperMajstri.
                  Údaje nikdy nezdieľame.
                </span>
              </label>

              {status === "error" && message && (
                <p className="text-xs text-[#FF5E00]" data-testid="lead-magnet-error">
                  {message}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full btn-primary disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                data-testid="lead-magnet-submit"
              >
                {status === "loading" ? (
                  <>Posielame PDF…</>
                ) : (
                  <><Download size={16} /> Chcem PDF zdarma</>
                )}
              </button>

              <p className="text-[11px] text-zinc-600 text-center pt-1">
                Pošleme vám iba toto PDF. Žiadny spam, žiaden odber newslettra.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
